import { Layout } from "@/components/layout/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TESTIDS } from "@/constants/testIds";
import { TUTORIAL_STEPS } from "@/data/tutorial";
import { useMemo, useState } from "react";
import { applyMove, createInitialState, getLegalMoves, isLegalMove } from "@/game/engine";
import { Board } from "@/components/board/Board";
import { useSettings } from "@/hooks/useSettings";
import { ArrowLeft, ArrowRight, CheckCircle2, Play, RotateCcw } from "lucide-react";
import { markTutorialCompleted } from "@/hooks/useStats";

export function Learn() {
  const { settings } = useSettings();
  const [stepIdx, setStepIdx] = useState(0);
  const step = TUTORIAL_STEPS[stepIdx];

  // For interactive steps we track a sandbox state.
  const [sandbox, setSandbox] = useState(() => (step.interactive ? step.setup() : null));
  const [stepDone, setStepDone] = useState(false);

  const advance = () => {
    if (stepIdx >= TUTORIAL_STEPS.length - 1) {
      markTutorialCompleted();
      return;
    }
    const next = TUTORIAL_STEPS[stepIdx + 1];
    setSandbox(next.interactive ? next.setup() : null);
    setStepDone(false);
    setStepIdx((i) => i + 1);
  };
  const back = () => {
    if (stepIdx === 0) return;
    const prev = TUTORIAL_STEPS[stepIdx - 1];
    setSandbox(prev.interactive ? prev.setup() : null);
    setStepDone(false);
    setStepIdx((i) => i - 1);
  };
  const restart = () => {
    setSandbox(step.interactive ? step.setup() : null);
    setStepDone(false);
  };

  const legalSet = useMemo(() => {
    if (!sandbox) return new Set();
    if (step.requireMove) {
      // restrict legal set to the required move only
      const [r, c] = step.requireMove;
      if (isLegalMove(sandbox, r, c)) return new Set([`${r},${c}`]);
    }
    return new Set(getLegalMoves(sandbox).map(([r, c]) => `${r},${c}`));
  }, [sandbox, step]);

  const onPitSelect = (r, c) => {
    if (!sandbox) return;
    if (step.requireMove && (r !== step.requireMove[0] || c !== step.requireMove[1])) return;
    try {
      const { state } = applyMove(sandbox, r, c);
      setSandbox(state);
      setStepDone(true);
    } catch (e) {
      // ignore
    }
  };

  return (
    <Layout>
      <section
        data-testid={TESTIDS.learnPage}
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10"
      >
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Tutorial</p>
        <h1 className="font-display text-4xl sm:text-5xl mt-1">Learn to play Tsoro</h1>

        <div className="mt-6 flex items-center gap-2 flex-wrap">
          {TUTORIAL_STEPS.map((s, i) => (
            <Badge
              key={s.id}
              variant={i === stepIdx ? "default" : "outline"}
              data-testid={TESTIDS.tutorialStep(i + 1)}
              className="rounded-full text-[10px] uppercase tracking-widest"
            >
              {i + 1}. {s.title}
            </Badge>
          ))}
        </div>

        <div className="mt-8 grid lg:grid-cols-12 gap-6">
          <Card className="lg:col-span-5 p-6">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">
              Step {stepIdx + 1} of {TUTORIAL_STEPS.length}
            </p>
            <h2 className="font-display text-2xl mt-1">{step.title}</h2>
            <p className="mt-3 text-foreground/80 leading-relaxed">{step.body}</p>
            {step.hint && (
              <p className="mt-3 text-sm text-primary">{step.hint}</p>
            )}
            {step.interactive && stepDone && (
              <p className="mt-4 inline-flex items-center gap-2 text-success font-medium text-sm">
                <CheckCircle2 className="h-4 w-4 text-[hsl(var(--highlight-legal))]" /> Move completed.
              </p>
            )}

            <div className="mt-6 flex flex-wrap gap-2">
              <Button
                data-testid={TESTIDS.tutorialPrevBtn}
                variant="outline"
                onClick={back}
                disabled={stepIdx === 0}
                className="rounded-full"
              >
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
              <Button
                data-testid={TESTIDS.tutorialNextBtn}
                onClick={advance}
                disabled={step.interactive && !stepDone}
                className="rounded-full"
              >
                {stepIdx === TUTORIAL_STEPS.length - 1 ? (
                  <>
                    <Play className="h-4 w-4 mr-2" /> Finish
                  </>
                ) : (
                  <>
                    Next <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
              {step.interactive && (
                <Button
                  data-testid={TESTIDS.tutorialRestartBtn}
                  variant="ghost"
                  onClick={restart}
                  className="rounded-full"
                >
                  <RotateCcw className="h-4 w-4 mr-2" /> Reset step
                </Button>
              )}
            </div>
          </Card>

          <div className="lg:col-span-7">
            {sandbox ? (
              <Board
                board={sandbox.board}
                currentPlayer={sandbox.currentPlayer}
                legalSet={legalSet}
                onPitSelect={onPitSelect}
                showHints
                coords={settings.coordLabels}
                disabled={false}
                highContrast={settings.highContrast}
                sowingDirection={sandbox.sowingDirection || "CCW"}
              />
            ) : (
              <Card className="p-6 board-surface rounded-3xl flex items-center justify-center min-h-[260px]">
                <p className="font-display text-2xl text-foreground/80 text-center">
                  Read the panel, then press <span className="text-primary">Next</span> when ready.
                </p>
              </Card>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}
